import React, { useMemo, useState } from "react";
import "./CategoryMenuBar.css";

/**
 * CategoryMenuBar Component
 *
 * Reusable category navigation component with:
 * - Desktop: Sidebar with category list
 * - Mobile: Fixed menu bar + slide-out menu
 * - Consistent styling across product pages
 *
 * @param {Array} categories - Array of category objects [{value, label, icon}]
 * @param {String} selectedCategory - Currently active category value
 * @param {Function} onCategoryClick - Callback when category is clicked
 * @param {String} position - 'fixed' or 'sticky' for menu bar position (default: 'fixed')
 * @param {String} mode - 'full' (default), 'mobile-only', or 'sidebar-only'
 */
const CategoryMenuBar = ({
  categories = [],
  selectedCategory = "all",
  onCategoryClick,
  position = "fixed",
  mode = "full",
  categoryCounts = {},
  showCategoriesLabel = true,
  initialVisibleCount = 12,
}) => {
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const defaultVisibleCategories =
    Number.isFinite(initialVisibleCount) && initialVisibleCount > 0
      ? Math.floor(initialVisibleCount)
      : 12;

  const normalizeCategoryKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

  const selectedCategoryObj =
    selectedCategory === "all"
      ? { label: "All Products", icon: "🛍️", value: "all" }
      : categories.find((c) => c.value === selectedCategory) || {
          label: "Category",
          icon: "📦",
          value: selectedCategory,
        };

  const selectedCount =
    selectedCategory === "all"
      ? categoryCounts.all
      : categoryCounts[normalizeCategoryKey(selectedCategoryObj.value)];

  const toggleCategoriesModal = () => {
    setIsCategoriesOpen(!isCategoriesOpen);
  };

  const { visibleCategories, hiddenCategoriesCount } = useMemo(() => {
    const categoryList = Array.isArray(categories) ? categories.filter(Boolean) : [];

    if (showAllCategories || categoryList.length <= defaultVisibleCategories) {
      return {
        visibleCategories: categoryList,
        hiddenCategoriesCount: 0,
      };
    }

    const prioritized = [];
    const seenValues = new Set();

    const pushCategory = (category) => {
      if (!category?.value || seenValues.has(category.value)) {
        return;
      }

      seenValues.add(category.value);
      prioritized.push(category);
    };

    if (selectedCategory !== "all") {
      pushCategory(
        categoryList.find((category) => category.value === selectedCategory),
      );
    }

    categoryList.forEach((category) => {
      if (prioritized.length < defaultVisibleCategories) {
        pushCategory(category);
      }
    });

    return {
      visibleCategories: prioritized,
      hiddenCategoriesCount: Math.max(categoryList.length - prioritized.length, 0),
    };
  }, [categories, selectedCategory, showAllCategories]);

  const handleCategoryClick = (categoryValue) => {
    if (onCategoryClick) {
      onCategoryClick(categoryValue);
    }
    setShowAllCategories(false);
    setIsCategoriesOpen(false); // Close mobile menu after selection
  };

  const handleToggleShowAll = () => {
    setShowAllCategories((prev) => !prev);
  };

  return (
    <>
      {/* Category Menu Bar Label (Mobile) - only in mobile-only or full mode */}
      {(mode === "mobile-only" || mode === "full") && (
        <>
          <div className={`category-menu-bar ${position}`}>
            <div className="category-menu-bar-content">
              <button
                type="button"
                className={`category-menu-trigger${showCategoriesLabel ? "" : " category-menu-trigger--compact"}`}
                onClick={toggleCategoriesModal}
              >
                {showCategoriesLabel && (
                  <span className="category-menu-trigger-left">
                    <span className="category-menu-icon">📂</span>
                    <span className="category-menu-text">Categories</span>
                  </span>
                )}
                <span className="category-menu-trigger-right">
                  <span className="category-menu-active-chip">
                    {selectedCategoryObj.icon || "📦"}{" "}
                    {selectedCategoryObj.label}
                    {typeof selectedCount === "number"
                      ? ` (${selectedCount})`
                      : ""}
                  </span>
                  <span className="category-menu-caret">▾</span>
                </span>
              </button>
            </div>
          </div>

          {/* Mobile Categories Menu - Slide-out like navbar */}
          <div
            className={`categories-menu-overlay ${isCategoriesOpen ? "open" : ""}`}
            onClick={toggleCategoriesModal}
          ></div>

          <nav
            className={`categories-side-menu ${isCategoriesOpen ? "open" : ""}`}
          >
            <div className="categories-menu-header">
              <span className="menu-logo">
                {showCategoriesLabel ? "📂 Categories" : "All Products"}
              </span>
              <button className="close-btn" onClick={toggleCategoriesModal}>
                &times;
              </button>
            </div>
            <div className="categories-menu-items">
              <button
                className={`category-menu-item ${selectedCategory === "all" ? "active" : ""}`}
                onClick={() => handleCategoryClick("all")}
              >
                <span className="category-icon">🛍️</span>
                <span className="category-label">All Products</span>
                {typeof categoryCounts.all === "number" && (
                  <span className="category-count">{categoryCounts.all}</span>
                )}
              </button>
              {visibleCategories.map((category) => (
                <button
                  key={category.value}
                  className={`category-menu-item ${selectedCategory === category.value ? "active" : ""}`}
                  onClick={() => handleCategoryClick(category.value)}
                >
                  <span className="category-icon">{category.icon || "📦"}</span>
                  <span className="category-label">{category.label}</span>
                  {typeof categoryCounts[
                    String(category.value)
                      .trim()
                      .toLowerCase()
                      .replace(/[\s-]+/g, "_")
                  ] === "number" && (
                    <span className="category-count">
                      {
                        categoryCounts[
                          String(category.value)
                            .trim()
                            .toLowerCase()
                            .replace(/[\s-]+/g, "_")
                        ]
                      }
                    </span>
                  )}
                </button>
              ))}
              {hiddenCategoriesCount > 0 && (
                <button
                  type="button"
                  className="category-menu-toggle"
                  onClick={handleToggleShowAll}
                >
                  Show {hiddenCategoriesCount} more categories
                </button>
              )}
              {showAllCategories && categories.length > defaultVisibleCategories && (
                <button
                  type="button"
                  className="category-menu-toggle category-menu-toggle--secondary"
                  onClick={handleToggleShowAll}
                >
                  Show fewer categories
                </button>
              )}
            </div>
          </nav>
        </>
      )}

      {/* Categories Sidebar (Desktop) - only in sidebar-only or full mode */}
      {(mode === "sidebar-only" || mode === "full") && (
        <aside className="categories-sidebar">
          <div className="categories-header">
            <h3>{showCategoriesLabel ? "Categories" : "All Products"}</h3>
          </div>
          <div className="categories-list">
            <button
              className={`category-item ${selectedCategory === "all" ? "active" : ""}`}
              onClick={() => handleCategoryClick("all")}
            >
              <span className="category-icon">🛍️</span>
              <span className="category-label">All Products</span>
              {typeof categoryCounts.all === "number" && (
                <span className="category-count">{categoryCounts.all}</span>
              )}
            </button>
            {visibleCategories.map((category) => (
              <button
                key={category.value}
                className={`category-item ${selectedCategory === category.value ? "active" : ""}`}
                onClick={() => handleCategoryClick(category.value)}
              >
                <span className="category-icon">{category.icon || "📦"}</span>
                <span className="category-label">{category.label}</span>
                {typeof categoryCounts[
                  String(category.value)
                    .trim()
                    .toLowerCase()
                    .replace(/[\s-]+/g, "_")
                ] === "number" && (
                  <span className="category-count">
                    {
                      categoryCounts[
                        String(category.value)
                          .trim()
                          .toLowerCase()
                          .replace(/[\s-]+/g, "_")
                      ]
                    }
                  </span>
                )}
              </button>
            ))}
            {hiddenCategoriesCount > 0 && (
              <button
                type="button"
                className="category-list-toggle"
                onClick={handleToggleShowAll}
              >
                Show {hiddenCategoriesCount} more categories
              </button>
            )}
            {showAllCategories && categories.length > defaultVisibleCategories && (
              <button
                type="button"
                className="category-list-toggle category-list-toggle--secondary"
                onClick={handleToggleShowAll}
              >
                Show fewer categories
              </button>
            )}
          </div>
        </aside>
      )}
    </>
  );
};

export default CategoryMenuBar;
